import { Autocomplete, Box, TextField } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import React, { useEffect, useState } from "react"
import { Controller, useWatch } from "react-hook-form"

import { FIELD_LABEL_CLASSES } from "@/constants/forms"

type GenericOption = Record<string, any>

interface FormAutocompleteProps {
  control: any
  name: string
  label?: string
  options: GenericOption[]
  valueKey?: string
  labelKey?: string
  onLoadMore?: () => void
  setSearchTerm: (value: string) => void
  renderOption?: (props: any, option: GenericOption, state: any) => React.ReactNode
  multiple?: boolean
  disabled?: boolean
  renderTags?: (value: GenericOption[], getTagProps: any) => React.ReactNode
  placeholder?: string
  onChange?: (value: any) => void
}

const scrollThreshold = 10

const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
  control,
  name,
  label,
  options,
  valueKey = "value",
  labelKey = "label",
  onLoadMore,
  setSearchTerm,
  renderOption,
  multiple = false,
  disabled = false,
  renderTags,
  placeholder = "Select One",
  onChange,
}) => {
  const [localInputValue, setLocalInputValue] = useState("")
  const [isInitialValue, setIsInitialValue] = useState(true)

  const fieldValue = useWatch({
    control,
    name,
  })

  // Reset searchTerm and localInputValue on unmount
  useEffect(() => {
    return () => {
      setSearchTerm("")
      setLocalInputValue("")
    }
  }, [setSearchTerm])

  useEffect(() => {
    if (!fieldValue) {
      setLocalInputValue("")
    }
  }, [fieldValue])

  return (
    <Box className='w-full'>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const currentValue = multiple
            ? options.filter((item) => Array.isArray(field.value) && field.value.includes(item[valueKey]))
            : (options.find((item) => item[valueKey] === field.value) ?? null)

          const handleChange = (_: unknown, newValue: GenericOption | GenericOption[] | null) => {
            if (multiple) {
              const newValues = newValue as GenericOption[]
              const mappedValues = newValues.map((item) => item[valueKey])
              field.onChange(mappedValues)
              onChange?.(mappedValues)
              setLocalInputValue("")
            } else {
              const newVal = newValue ? (newValue as GenericOption)[valueKey] : ""
              field.onChange(newVal)
              onChange?.(newVal)
              setLocalInputValue(newValue ? (newValue as GenericOption)[labelKey] : "")
            }
          }

          const handleInputChange = (_: React.SyntheticEvent<Element, Event>, newValue: string, reason: string) => {
            const isResetFromCurrent =
              reason === "reset" &&
              !multiple &&
              (currentValue as GenericOption)?.[labelKey] === newValue &&
              isInitialValue

            if (isResetFromCurrent) {
              setLocalInputValue(newValue)
            } else if (reason !== "selectOption" && reason !== "reset") {
              setLocalInputValue(newValue)
              setSearchTerm(newValue)
              setIsInitialValue(false)
            }
          }

          return (
            <>
              {label && <label className={FIELD_LABEL_CLASSES}>{label}</label>}
              <Autocomplete
                multiple={multiple}
                value={currentValue}
                onChange={handleChange}
                inputValue={localInputValue}
                onInputChange={handleInputChange}
                options={options}
                getOptionLabel={(option) => option[labelKey] ?? ""}
                popupIcon={<KeyboardArrowDownOutlined />}
                renderOption={renderOption}
                renderTags={renderTags}
                disabled={disabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    size='small'
                    onKeyDown={(e: any) => {
                      if (multiple && localInputValue === "" && e.key === "Backspace") {
                        e.stopPropagation()
                      }
                      if (params?.inputProps && params?.inputProps?.onKeyDown) {
                        params.inputProps.onKeyDown(e)
                      }
                    }}
                  />
                )}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "setPopperWidth",
                        enabled: true,
                        phase: "beforeWrite",
                        fn: ({ state }) => {
                          state.styles.popper.width = "auto"
                        },
                      },
                    ],
                    sx: { width: "auto !important" },
                  },
                  paper: {
                    sx: { minWidth: "max-content" },
                  },
                  listbox: {
                    sx: { "& li": { whiteSpace: "nowrap" } },
                  },
                }}
                ListboxProps={{
                  onScroll: (event) => {
                    const el = event.currentTarget
                    if (onLoadMore && el.scrollTop + el.clientHeight >= el.scrollHeight - scrollThreshold) {
                      onLoadMore()
                    }
                  },
                  style: {
                    maxHeight: 300,
                  },
                }}
              />
            </>
          )
        }}
      />
    </Box>
  )
}

export default FormAutocomplete
